import React, { useState, useEffect, useContext } from "react";
import {
  TextField, Button, Grid, Box, CircularProgress, Snackbar, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  useMediaQuery, TableContainer, Paper, Typography, Dialog,
  DialogActions, DialogContent, DialogTitle, InputAdornment, 
  alpha, Chip, Avatar, Card, CardContent, Fade, Zoom, Tooltip, 
  Badge, styled, Drawer, FormControlLabel, Switch, Select, 
  MenuItem, FormControl, InputLabel, Tabs, Tab, Divider,
  List, ListItem, ListItemText, ListItemIcon, Checkbox
} from "@mui/material";
import {
  Edit, Save, Cancel, Delete, Search, Visibility, VisibilityOff,
  People, PersonAdd, Security, AdminPanelSettings, Lock,
  History, MoreVert, Settings, Shield, Key, Group,
  CheckCircle, Warning, Info, Close, Add
} from "@mui/icons-material";
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, query, where } from "firebase/firestore";
import { db } from "../firebase-config";
import Collections from "../collections";
import appTheme from '../theme';
import { USER_ROLES, PERMISSIONS, hasPermission, canEditField } from '../utils/userPermissions';

// **STYLED COMPONENTS** (keeping previous styles + new ones)
const MainContainer = styled(Box)({
  minHeight: '100vh',
  background: appTheme.gradients?.luxury || appTheme.colors.surfaceLuxury,
  padding: appTheme.spacing?.xl || '32px',
});

const EditDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: 500,
    maxWidth: '90vw',
    padding: appTheme.spacing?.xl || '32px',
    background: appTheme.gradients?.cardLuxury || appTheme.colors.surface,
  },
});

const PermissionCard = styled(Card)({
  marginBottom: appTheme.spacing?.md || '20px',
  border: `1px solid ${appTheme.colors?.borderSubtle || '#f4f6f7'}`,
  borderRadius: appTheme.borderRadius?.medium || '10px',
  '&:hover': {
    boxShadow: appTheme.shadows?.soft || '0 2px 6px rgba(0,0,0,0.06)',
  }
});

const RoleChip = styled(Chip)({
  fontWeight: appTheme.typography?.fontWeights?.bold || 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

const ManageUsers = () => {
  // **EXISTING STATE + NEW STATE FOR ACCESS CONTROL**
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showPasswordIds, setShowPasswordIds] = useState({});

  // **NEW: ADVANCED EDIT CONTROL STATE**
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editedUser, setEditedUser] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(USER_ROLES.ADMIN); // Get from auth context
  const [editTab, setEditTab] = useState(0);
  const [auditLogs, setAuditLogs] = useState([]);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [roleChangeDialog, setRoleChangeDialog] = useState(false);

  const isMobile = useMediaQuery("(max-width:600px)");

  // **GET ROLE-BASED STYLING**
  const getRoleColor = (role) => {
    switch (role) {
      case USER_ROLES.SUPER_ADMIN: return appTheme.colors?.error || '#c0392b';
      case USER_ROLES.ADMIN: return appTheme.colors?.primary || '#1a4d3a';
      case USER_ROLES.MANAGER: return appTheme.colors?.secondary || '#8b1538';
      case USER_ROLES.VIEWER: return appTheme.colors?.textSecondary || '#5d6d7e';
      default: return appTheme.colors?.textMuted || '#8b7d73';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case USER_ROLES.SUPER_ADMIN: return <Shield />;
      case USER_ROLES.ADMIN: return <AdminPanelSettings />;
      case USER_ROLES.MANAGER: return <Group />;
      case USER_ROLES.VIEWER: return <Visibility />;
      default: return <People />;
    }
  };

  // **ENHANCED USER FETCHING WITH ROLE SUPPORT**
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const usersSnapshot = await getDocs(collection(db, Collections.USERS));
        const usersList = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          role: USER_ROLES.VIEWER, // Default role
          status: 'active',
          lastLogin: null,
          createdBy: 'system',
          ...doc.data(),
        }));
        
        console.log("📊 Fetched users with roles:", usersList.length);
        setUsers(usersList);
        setFilteredUsers(usersList);
      } catch (error) {
        console.error("Error fetching users:", error);
        setToast({
          open: true,
          message: "Failed to load users. Please refresh the page.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // **ENHANCED SEARCH WITH ROLE FILTERING**
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(user => 
        (user.name?.toLowerCase().includes(query)) ||
        (user.email?.toLowerCase().includes(query)) ||
        (user.restaurantName?.toLowerCase().includes(query)) ||
        (user.role?.toLowerCase().includes(query)) ||
        (user.phone?.includes(query))
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users]);

  // **ADVANCED EDIT FUNCTIONS**
  const openEditDrawer = (user) => {
    if (!hasPermission(currentUserRole, PERMISSIONS.EDIT_USER_INFO)) {
      setToast({
        open: true,
        message: "❌ You don't have permission to edit users.",
        severity: "error",
      });
      return;
    }

    setSelectedUser(user);
    setEditedUser({ ...user });
    setEditDrawerOpen(true);
    setEditTab(0);
    fetchAuditLogs(user.id);
  };

  const closeEditDrawer = () => {
    setEditDrawerOpen(false);
    setSelectedUser(null);
    setEditedUser(null);
    setEditTab(0);
    setAuditLogs([]);
  };

  const handleFieldChange = (fieldName, value) => {
    if (!canEditField(currentUserRole, fieldName)) {
      setToast({
        open: true,
        message: `❌ You don't have permission to edit ${fieldName}.`,
        severity: "warning",
      });
      return;
    }

    setEditedUser(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const updateRestaurantInvoices = async (restaurantId, updateFields) => {
  try {
    console.log(`🔄 Updating invoices for restaurant ${restaurantId} with:`, updateFields);
    
    // Query all invoices for this restaurant
    const invoicesQuery = query(
      collection(db, "invoices"), 
      where("userId", "==", restaurantId)
    );
    
    const querySnapshot = await getDocs(invoicesQuery);
    console.log(`📋 Found ${querySnapshot.size} invoices to update`);
    
    if (querySnapshot.size === 0) {
      console.log("ℹ️ No invoices found for this restaurant");
      return;
    }
    
    // Update each invoice
    const updatePromises = querySnapshot.docs.map(async (invoiceDoc) => {
      const invoiceRef = doc(db, "invoices", invoiceDoc.id);
      await updateDoc(invoiceRef, {
        ...updateFields,
        updatedAt: new Date().toISOString(),
        invoiceUpdatedBy: currentUserRole, // Track who updated the invoice
        invoiceUpdateReason: "Restaurant information changed"
      });
      console.log(`✅ Updated invoice: ${invoiceDoc.id}`);
    });
    
    await Promise.all(updatePromises);
    console.log(`🎉 Successfully updated ${querySnapshot.size} invoices`);
    
  } catch (error) {
    console.error("❌ Error updating restaurant invoices:", error);
    throw new Error(`Failed to update related invoices: ${error.message}`);
  }
};

  const saveUserChanges = async () => {
  if (!selectedUser || !editedUser) return;

  try {
    const userDocRef = doc(db, Collections.USERS, selectedUser.id);
    
    // **DETECT CHANGES THAT NEED TO PROPAGATE TO INVOICES**
    const changes = {};
    const invoiceUpdateFields = {};
    
    // Check which fields changed and need to update invoices
    if (editedUser.restaurantName !== selectedUser.restaurantName) {
      changes.restaurantName = editedUser.restaurantName;
      invoiceUpdateFields.restaurantName = editedUser.restaurantName;
    }
    
    if (editedUser.name !== selectedUser.name) {
      changes.name = editedUser.name;
      // If this is the restaurant owner name, update in invoices
      invoiceUpdateFields.name = editedUser.name;
    }
    
    if (editedUser.email !== selectedUser.email) {
      changes.email = editedUser.email;
      invoiceUpdateFields.email = editedUser.email;
    }
    
    if (editedUser.phone !== selectedUser.phone) {
      changes.phone = editedUser.phone;
      invoiceUpdateFields.phone = editedUser.phone;
    }
    
    if (editedUser.address !== selectedUser.address) {
      changes.address = editedUser.address;
      // Optionally update address in invoices if needed
      // invoiceUpdateFields.address = editedUser.address;
    }

    const updateData = {
      ...editedUser,
      updatedAt: new Date().toISOString(),
      updatedBy: currentUserRole,
    };

    // **STEP 1: UPDATE USER DOCUMENT**
    await updateDoc(userDocRef, updateData);

    // **STEP 2: UPDATE ALL INVOICES FOR THIS RESTAURANT IF NEEDED**
    if (Object.keys(invoiceUpdateFields).length > 0) {
      await updateRestaurantInvoices(selectedUser.id, invoiceUpdateFields);
    }

    // **STEP 3: LOG THE CHANGE**
    await logUserChange(selectedUser.id, 'UPDATE', {
      ...updateData,
      invoiceUpdates: Object.keys(invoiceUpdateFields).length > 0 ? invoiceUpdateFields : 'No invoice updates needed'
    });

    // **STEP 4: UPDATE LOCAL STATE**
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === selectedUser.id ? { ...user, ...updateData } : user
      )
    );

    setToast({
      open: true,
      message: `✅ ${editedUser.name}'s information updated successfully!${
        Object.keys(invoiceUpdateFields).length > 0 
          ? ` ${Object.keys(invoiceUpdateFields).length} field(s) updated in related invoices.` 
          : ''
      }`,
      severity: "success",
    });

    closeEditDrawer();
  } catch (error) {
    console.error("Error updating user:", error);
    setToast({
      open: true,
      message: "Failed to update user information. Please try again.",
      severity: "error",
    });
  }
};

  const logUserChange = async (userId, action, data) => {
    try {
      await addDoc(collection(db, 'auditLogs'), {
        userId,
        action,
        changes: data,
        timestamp: new Date().toISOString(),
        performedBy: currentUserRole, // In real app, get current user ID
        userAgent: navigator.userAgent,
      });
    } catch (error) {
      console.error("Error logging change:", error);
    }
  };

  const fetchAuditLogs = async (userId) => {
    try {
      // In real implementation, fetch from auditLogs collection
      // For now, using mock data
      const mockLogs = [
        {
          id: 1,
          action: 'UPDATE',
          timestamp: new Date(Date.now() - 86400000).toISOString(),
          performedBy: 'admin@example.com',
          changes: { name: 'Updated name', phone: 'Updated phone' }
        },
        {
          id: 2,
          action: 'CREATE',
          timestamp: new Date(Date.now() - 172800000).toISOString(),
          performedBy: 'system',
          changes: { status: 'User created' }
        }
      ];
      setAuditLogs(mockLogs);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    }
  };

  const handleBulkRoleChange = async (newRole) => {
    if (!hasPermission(currentUserRole, PERMISSIONS.MANAGE_ROLES)) {
      setToast({
        open: true,
        message: "❌ You don't have permission to manage roles.",
        severity: "error",
      });
      return;
    }

    try {
      const promises = selectedUsers.map(userId => {
        const userDocRef = doc(db, Collections.USERS, userId);
        return updateDoc(userDocRef, {
          role: newRole,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUserRole,
        });
      });

      await Promise.all(promises);

      setUsers(prevUsers =>
        prevUsers.map(user =>
          selectedUsers.includes(user.id)
            ? { ...user, role: newRole }
            : user
        )
      );

      setToast({
        open: true,
        message: `✅ Successfully updated ${selectedUsers.length} user(s) role to ${newRole}`,
        severity: "success",
      });

      setSelectedUsers([]);
      setBulkEditMode(false);
      setRoleChangeDialog(false);
    } catch (error) {
      console.error("Error updating roles:", error);
      setToast({
        open: true,
        message: "Failed to update user roles. Please try again.",
        severity: "error",
      });
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const deleteUser = async (user) => {
  if (!hasPermission(currentUserRole, PERMISSIONS.DELETE_USER)) {
    setToast({
      open: true,
      message: "❌ You don't have permission to delete users.",
      severity: "error",
    });
    return;
  }

  try {
    const userDocRef = doc(db, Collections.USERS, user.id);
    
    // **OPTIONAL: Check if user has invoices before deletion**
    const invoicesQuery = query(
      collection(db, "invoices"), 
      where("userId", "==", user.id)
    );
    const invoicesSnapshot = await getDocs(invoicesQuery);
    
    if (invoicesSnapshot.size > 0) {
      // Option 1: Ask for confirmation
      if (!window.confirm(
        `This user has ${invoicesSnapshot.size} invoices. Deleting will remove user data but invoices will remain with restaurant ID reference. Continue?`
      )) {
        return;
      }
      
      // Option 2: Update invoices to mark as orphaned
      // await updateRestaurantInvoices(user.id, { 
      //   restaurantStatus: "deleted",
      //   originalRestaurantName: user.restaurantName 
      // });
    }
    
    await deleteDoc(userDocRef);
    
    await logUserChange(user.id, 'DELETE', { 
      deletedUser: user,
      affectedInvoices: invoicesSnapshot.size 
    });

    setUsers(prevUsers => prevUsers.filter(u => u.id !== user.id));
    setFilteredUsers(prevUsers => prevUsers.filter(u => u.id !== user.id));
    
    setToast({
      open: true,
      message: `🗑️ ${user.name} has been removed from the system.${
        invoicesSnapshot.size > 0 ? ` ${invoicesSnapshot.size} invoices retain reference.` : ''
      }`,
      severity: "success",
    });
  } catch (error) {
    console.error("Error deleting user:", error);
    setToast({
      open: true,
      message: "Failed to delete user. Please try again.",
      severity: "error",
    });
  }
};

  // **RENDER PERMISSION-BASED FIELD**
  const renderEditableField = (fieldName, label, value, type = 'text', options = null) => {
    const canEdit = canEditField(currentUserRole, fieldName);
    
    if (type === 'select' && options) {
      return (
        <FormControl fullWidth margin="normal">
          <InputLabel>{label}</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            disabled={!canEdit}
            label={label}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        fullWidth
        margin="normal"
        label={label}
        value={value || ''}
        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
        disabled={!canEdit}
        type={type}
        InputProps={{
          endAdornment: !canEdit ? (
            <InputAdornment position="end">
              <Tooltip title="You don't have permission to edit this field">
                <Lock sx={{ color: appTheme.colors?.textMuted || '#8b7d73' }} />
              </Tooltip>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: canEdit 
              ? appTheme.colors?.surface || '#ffffff' 
              : alpha(appTheme.colors?.platinum || '#e8eaed', 0.5),
          },
        }}
      />
    );
  };

  return (
    <MainContainer>
      {/* **ENHANCED HEADER WITH BULK ACTIONS** */}
      <Fade in timeout={600}>
        <Card sx={{ 
          background: appTheme.gradients?.hero || appTheme.colors.primary,
          color: appTheme.colors?.champagne || '#f7e7ce',
          mb: 3,
          borderRadius: appTheme.borderRadius?.rounded || '16px',
        }}>
          <CardContent sx={{ padding: appTheme.spacing?.xl || '32px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 64, height: 64, background: appTheme.colors?.champagne || '#f7e7ce', color: appTheme.colors?.primary || '#1a4d3a' }}>
                  <People fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: appTheme.typography?.fontWeights?.black || 900, mb: 1 }}>
                    User Management System
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Advanced user control with role-based permissions
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <RoleChip
                  icon={getRoleIcon(currentUserRole)}
                  label={`Current Role: ${currentUserRole}`}
                  sx={{
                    backgroundColor: getRoleColor(currentUserRole),
                    color: 'white',
                  }}
                />
                
                {hasPermission(currentUserRole, PERMISSIONS.BULK_OPERATIONS) && (
                  <Button
                    variant="outlined"
                    startIcon={<Settings />}
                    onClick={() => setBulkEditMode(!bulkEditMode)}
                    sx={{
                      borderColor: appTheme.colors?.champagne || '#f7e7ce',
                      color: appTheme.colors?.champagne || '#f7e7ce',
                      '&:hover': {
                        backgroundColor: alpha(appTheme.colors?.champagne || '#f7e7ce', 0.1),
                      },
                    }}
                  >
                    {bulkEditMode ? 'Exit Bulk Mode' : 'Bulk Edit'}
                  </Button>
                )}
              </Box>
            </Box>

            {/* **BULK ACTIONS BAR** */}
            {bulkEditMode && (
              <Box sx={{ mt: 3, p: 2, backgroundColor: alpha(appTheme.colors?.champagne || '#f7e7ce', 0.1), borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h6">
                    Bulk Actions ({selectedUsers.length} selected)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<Group />}
                      onClick={() => setRoleChangeDialog(true)}
                      disabled={selectedUsers.length === 0}
                      sx={{ color: appTheme.colors?.champagne || '#f7e7ce' }}
                    >
                      Change Roles
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setSelectedUsers([])}
                      sx={{ color: appTheme.colors?.champagne || '#f7e7ce' }}
                    >
                      Clear Selection
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* **ENHANCED SEARCH & FILTERS** */}
      <Card sx={{ mb: 3, borderRadius: appTheme.borderRadius?.medium || '10px' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Search by name, email, restaurant, role, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: appTheme.colors?.primary || '#1a4d3a' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Chip label={`${filteredUsers.length} users`} color="primary" />
                <Chip 
                  label={`${users.filter(u => u.role === USER_ROLES.ADMIN).length} Admins`} 
                  sx={{ backgroundColor: getRoleColor(USER_ROLES.ADMIN), color: 'white' }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* **ENHANCED USERS TABLE** */}
      <Card sx={{ borderRadius: appTheme.borderRadius?.rounded || '16px', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ background: appTheme.gradients?.hero || appTheme.colors.primary }}>
              <TableRow>
                {bulkEditMode && (
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <Checkbox
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers(filteredUsers.map(u => u.id));
                        } else {
                          setSelectedUsers([]);
                        }
                      }}
                      sx={{ color: 'white' }}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Contact & Business</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role & Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Security</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={bulkEditMode ? 6 : 5} align="center" sx={{ py: 8 }}>
                    <CircularProgress sx={{ color: appTheme.colors?.primary || '#1a4d3a' }} />
                    <Typography sx={{ mt: 2 }}>Loading users...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={bulkEditMode ? 6 : 5} align="center" sx={{ py: 8 }}>
                    <People sx={{ fontSize: 64, color: appTheme.colors?.textMuted || '#8b7d73', mb: 2 }} />
                    <Typography variant="h6">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow 
                    key={user.id}
                    sx={{
                      '&:hover': { backgroundColor: alpha(appTheme.colors?.goldenHour || '#faf6e8', 0.7) },
                      backgroundColor: selectedUsers.includes(user.id) 
                        ? alpha(appTheme.colors?.primary || '#1a4d3a', 0.1) 
                        : 'inherit'
                    }}
                  >
                    {bulkEditMode && (
                      <TableCell>
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                        />
                      </TableCell>
                    )}
                    
                    {/* **USER INFO CELL** */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            backgroundColor: getRoleColor(user.role),
                            color: 'white',
                            width: 48,
                            height: 48,
                          }}
                        >
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {user.name || 'No Name'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {user.id?.substring(0, 8)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* **CONTACT & BUSINESS CELL** */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2">📧 {user.email || 'No Email'}</Typography>
                        <Typography variant="body2">📞 {user.phone || 'No Phone'}</Typography>
                        <Typography variant="body2">🏪 {user.restaurantName || 'No Restaurant'}</Typography>
                      </Box>
                    </TableCell>

                    {/* **ROLE & STATUS CELL** */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <RoleChip
                          icon={getRoleIcon(user.role)}
                          label={user.role || 'No Role'}
                          size="small"
                          sx={{
                            backgroundColor: getRoleColor(user.role),
                            color: 'white',
                          }}
                        />
                        <Chip
                          label={user.status || 'active'}
                          size="small"
                          color={user.status === 'active' ? 'success' : 'default'}
                        />
                      </Box>
                    </TableCell>

                    {/* **SECURITY CELL** */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Key sx={{ fontSize: 16, color: appTheme.colors?.accent || '#d4af37' }} />
                        {hasPermission(currentUserRole, PERMISSIONS.VIEW_SENSITIVE_DATA) ? (
                          <>
                            <Typography variant="body2">
                              {showPasswordIds[user.id] ? user.password : '••••••••'}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => setShowPasswordIds(prev => ({ ...prev, [user.id]: !prev[user.id] }))}
                            >
                              {showPasswordIds[user.id] ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Protected
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* **ACTIONS CELL** */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Edit User">
                          <IconButton
                            onClick={() => openEditDrawer(user)}
                            disabled={!hasPermission(currentUserRole, PERMISSIONS.EDIT_USER_INFO)}
                            sx={{ color: appTheme.colors?.primary || '#1a4d3a' }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Delete User">
                          <IconButton
                            onClick={() => {
                              setUserToDelete(user);
                              setDeleteDialogOpen(true);
                            }}
                            disabled={!hasPermission(currentUserRole, PERMISSIONS.DELETE_USER)}
                            sx={{ color: appTheme.colors?.error || '#c0392b' }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* **ADVANCED EDIT DRAWER** */}
      <EditDrawer
        anchor="right"
        open={editDrawerOpen}
        onClose={closeEditDrawer}
      >
        {selectedUser && (
          <Box>
            {/* **DRAWER HEADER** */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    backgroundColor: getRoleColor(selectedUser.role),
                    color: 'white',
                    width: 56,
                    height: 56,
                  }}
                >
                  {selectedUser.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Edit User
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedUser.name} • {selectedUser.role}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={closeEditDrawer}>
                <Close />
              </IconButton>
            </Box>

            {/* **EDIT TABS** */}
            <Tabs
              value={editTab}
              onChange={(e, newValue) => setEditTab(newValue)}
              sx={{ mb: 3 }}
            >
              <Tab label="Basic Info" icon={<People />} />
              <Tab label="Security" icon={<Security />} />
              <Tab label="Audit Log" icon={<History />} />
            </Tabs>

            {/* **TAB CONTENT** */}
            {editTab === 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: appTheme.colors?.primary || '#1a4d3a' }}>
                  Personal & Business Information
                </Typography>
                
                {renderEditableField('name', 'Full Name', editedUser?.name)}
                {renderEditableField('email', 'Email Address', editedUser?.email, 'email')}
                {renderEditableField('phone', 'Phone Number', editedUser?.phone, 'tel')}
                {renderEditableField('restaurantName', 'Restaurant Name', editedUser?.restaurantName)}
                {renderEditableField('address', 'Address', editedUser?.address)}
                
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="h6" sx={{ mb: 2, color: appTheme.colors?.secondary || '#8b1538' }}>
                  System Settings
                </Typography>
                
                {renderEditableField('role', 'User Role', editedUser?.role, 'select', [
                  { value: USER_ROLES.VIEWER, label: 'Viewer' },
                  { value: USER_ROLES.MANAGER, label: 'Manager' },
                  { value: USER_ROLES.ADMIN, label: 'Admin' },
                  { value: USER_ROLES.SUPER_ADMIN, label: 'Super Admin' },
                ])}
                
                {renderEditableField('status', 'Account Status', editedUser?.status, 'select', [
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'suspended', label: 'Suspended' },
                ])}
              </Box>
            )}

            {editTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: appTheme.colors?.accent || '#d4af37' }}>
                  Security Settings
                </Typography>
                
                {hasPermission(currentUserRole, PERMISSIONS.EDIT_SECURITY) ? (
                  <>
                    {renderEditableField('password', 'Password', editedUser?.password, 'password')}
                    
                    <Card sx={{ mt: 3, p: 2, backgroundColor: alpha(appTheme.colors?.warning || '#f39c12', 0.1) }}>
                      <Typography variant="body2" sx={{ color: appTheme.colors?.warning || '#f39c12', fontWeight: 600 }}>
                        ⚠️ Password changes will require the user to log in again with the new password.
                      </Typography>
                    </Card>
                  </>
                ) : (
                  <Card sx={{ p: 3, textAlign: 'center' }}>
                    <Lock sx={{ fontSize: 48, color: appTheme.colors?.textMuted || '#8b7d73', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      Access Denied
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      You don't have permission to modify security settings.
                    </Typography>
                  </Card>
                )}
              </Box>
            )}

            {editTab === 2 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Audit Trail
                </Typography>
                
                {auditLogs.length === 0 ? (
                  <Typography color="textSecondary">No audit logs available</Typography>
                ) : (
                  <List>
                    {auditLogs.map((log) => (
                      <ListItem key={log.id} divider>
                        <ListItemIcon>
                          <History />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${log.action} by ${log.performedBy}`}
                          secondary={new Date(log.timestamp).toLocaleString()}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* **DRAWER ACTIONS** */}
            <Box sx={{ position: 'sticky', bottom: 0, pt: 3, mt: 4, borderTop: 1, borderColor: 'divider', backgroundColor: 'white' }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={closeEditDrawer}
                  sx={{ minWidth: 120 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={saveUserChanges}
                  disabled={!hasPermission(currentUserRole, PERMISSIONS.EDIT_USER_INFO)}
                  sx={{
                    minWidth: 120,
                    background: appTheme.gradients?.hero || appTheme.colors.primary,
                    color: appTheme.colors?.champagne || '#f7e7ce',
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </EditDrawer>

      {/* **BULK ROLE CHANGE DIALOG** */}
      <Dialog
        open={roleChangeDialog}
        onClose={() => setRoleChangeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change User Roles</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Select a new role for {selectedUsers.length} selected user(s):
          </Typography>
          <FormControl fullWidth>
            <InputLabel>New Role</InputLabel>
            <Select defaultValue="" label="New Role">
              {Object.values(USER_ROLES).map((role) => (
                <MenuItem key={role} value={role} onClick={() => handleBulkRoleChange(role)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getRoleIcon(role)}
                    {role}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleChangeDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* **DELETE CONFIRMATION DIALOG** */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: appTheme.colors?.error || '#c0392b' }}>
          ⚠️ Confirm User Deletion
        </DialogTitle>
        <DialogContent>
          {userToDelete && (
            <Box>
              <Typography sx={{ mb: 2 }}>
                You are about to permanently delete:
              </Typography>
              <Card sx={{ p: 2, backgroundColor: alpha(appTheme.colors?.error || '#c0392b', 0.1) }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ backgroundColor: appTheme.colors?.error || '#c0392b', color: 'white' }}>
                    {userToDelete.name?.charAt(0) || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{userToDelete.name}</Typography>
                    <Typography variant="body2">{userToDelete.email}</Typography>
                  </Box>
                </Box>
              </Card>
              <Typography sx={{ mt: 2, color: appTheme.colors?.error || '#c0392b' }}>
                This action cannot be undone.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              deleteUser(userToDelete);
              setDeleteDialogOpen(false);
            }}
            sx={{ backgroundColor: appTheme.colors?.error || '#c0392b', color: 'white' }}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* **TOAST NOTIFICATIONS** */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
          sx={{
            borderRadius: appTheme.borderRadius?.rounded || '16px',
            boxShadow: appTheme.shadows?.strong || '0 8px 20px rgba(0,0,0,0.1)',
            fontSize: appTheme.typography?.fontSizes?.medium || '18px',
            fontWeight: 700,
            minWidth: 350,
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </MainContainer>
  );
};

export default ManageUsers;
